import React, { useContext, useEffect, useState } from "react";
import {
  Flex,
  Container,
  Text,
  Box,
  Divider,
  SecondaryButton,
  PrimaryButton,
} from "flicket-ui";
import { useRouter } from "next/router";
import useSWR from "swr";
import {
  Layout,
  Footer,
  BottomBar,
  Menu,
  Seo,
  Icon,
  Tabs,
  Status,
  ContentContainer,
} from "~components";
import { BaseContext, UserContext } from "~context";
import { getError, sdk, showToast } from "~lib";
import {
  Information,
  NonLoggedInRenewalBanner,
  RenewalBanner,
  Schedule,
} from "~components/memberships";
import { LandingPage, MembershipKind, MembershipQuery } from "~graphql/sdk";
import Image from "~components/common/Image";
import useCaptureClick from "~telemetry/traces/useCaptureClick";
import {
  AttributeNames,
  InteractionNames,
} from "~telemetry/enums/AttributeNames";
import { DesktopSubHeader } from "~components/common/common.BaseSubHeader";
import EventHeader from "~components/common/common.EventHeader";
import { useOrganization } from "~hooks";

const getMembership = async (_: string, orgId: string, membershipId: string) =>
  sdk({ orgId }).membership({ id: membershipId });

const swrConfig = {
  shouldRetryOnError: false,
};

type EventVenue = MembershipQuery["membership"]["events"][number]["venue"];

const hasMembershipToRenewFetcher = async (_: string, orgId: string) =>
  sdk({ orgId })
    .hasMembershipToRenew()
    .then((res) => res.myMembershipTickets);

const MembershipId = () => {
  const router = useRouter();
  const { organization, t } = useOrganization();
  const { isSinglePage } = useContext(BaseContext);
  const { isLoggedIn } = useContext(UserContext);
  const [eventVenues, setEventVenues] = useState<EventVenue[]>([]);
  const [locations, setLocations] = useState("");
  const { data, error } = useSWR<MembershipQuery, Error>(
    organization?.id && router?.query?.membershipId
      ? ["memberships", organization?.id, router?.query?.membershipId]
      : null,
    getMembership,
    swrConfig
  );

  const captureClick = useCaptureClick({
    forwardToNest: true,
    eventId: router?.query.eventId as string,
    pageName: "membership-details-page",
  });

  const isLoading = !data && !error;
  const isInactive = data?.membership?.isActive === false;

  useEffect(() => {
    if (isInactive) {
      void router.push("/memberships");
    }
  }, [isInactive]);

  const { data: myMemberships } = useSWR(
    organization?.id && organization?.showMembershipRenewal
      ? ["hasMembershipToRenew", organization?.id]
      : null,
    hasMembershipToRenewFetcher
  );

  const currentMembershipRenewalInformation = myMemberships?.find(
    (membership) =>
      membership.membershipType?.membership.id === router.query.membershipId
  );

  const hasMembershipToRenew =
    !currentMembershipRenewalInformation?.hasRenewedMembership &&
    !!currentMembershipRenewalInformation?.membershipType?.membership
      .renewalMembership?.id;

  if (error) {
    showToast(getError(error, "graphQL"), "error");
  }

  const isDigitalMembership =
    data?.membership?.membershipKind === MembershipKind.Digital;

  const showBanner =
    organization?.showMembershipRenewal && isLoggedIn && hasMembershipToRenew;

  const showNonLoggedInBanner =
    organization?.showMembershipRenewal && !isLoggedIn;

  const membership = data?.membership;

  const cartRecovery = router.query.cartRecovery;

  const membershipReservationLink = cartRecovery
    ? `/memberships/${membership?.id}/reservation?cartRecovery=true`
    : `/memberships/${membership?.id}/reservation`;

  useEffect(() => {
    membership?.events.forEach((membershipEvent) => {
      const eventVenueList = eventVenues;
      eventVenueList.push(membershipEvent.venue);

      const newlist = Array.from(
        new Set(eventVenueList.map((venue) => venue.name))
      ).map((name) => {
        return {
          name: name,
          address: eventVenueList.find((venue) => venue.name === name).address,
        };
      });
      setEventVenues(newlist);
      setLocations(
        newlist
          .map((venue) => `${venue.name}, ${venue?.address?.city}`)
          .join(", ")
      );
    });
  }, [membership]);

  const tabs = membership?.showSchedule
    ? [
        {
          id: "information",
          title: "Information",
          content: <Information {...membership} />,
        },
        {
          id: "schedule",
          title: "Schedule",
          content: <Schedule {...membership} />,
        },
      ]
    : [
        {
          id: "information",
          title: "Information",
          content: <Information {...membership} />,
        },
      ];

  let primaryButtonLabel = isSinglePage
    ? "Buy Tickets"
    : `Buy ${t("membership_name")}`;

  if (organization.id === "2648ddb4-b50c-489f-a3eb-ae0249e3db3c") {
    primaryButtonLabel = "Join now";
  }

  return (
    <>
      <Seo
        title={`${t("membership_name")} detail`}
        description={`${t("membership_name")} detail page for`}
      />
      <Menu />
      <Status error={error} loading={isLoading || isInactive}>
        <Flex flexDirection="column" flex={1}>
          <Box d={{ _: "block", md: "none" }} pb={8}>
            <EventHeader
              type="membership"
              title={membership?.name}
              startDate={membership?.startDate}
              endDate={membership?.endDate}
              venue={membership?.venue}
              location={locations}
              accessibility={membership?.accessibility}
              image={membership?.banner}
              description={membership?.description}
              supportInfo={organization?.supportInfo}
              goBack={() =>
                void router.push(
                  organization?.landingPage === LandingPage.SingleEvent
                    ? "/"
                    : "/memberships"
                )
              }
              coordinates={`${membership?.venue?.location?.lat},${membership?.venue?.location?.lng}`}
            />
          </Box>
          <Flex
            flexDirection="column"
            flex={1}
            maxH={{ _: "calc(100vh - 73px)", md: "unset" }}
            overflowY={{ _: "auto", md: "visible" }}
          >
            <Layout layoutType="overlay">
              <ContentContainer
                variant="content"
                image={membership?.backgroundImage}
              >
                <Flex flexDir="column" pb={{ _: 2, md: 0 }} flex="1">
                  <DesktopSubHeader
                    title={membership?.name}
                    subTitle={locations}
                    isSinglePage={isSinglePage}
                    backToLinkText={`Back to ${t("membership_name_plural")}`}
                    backToLink="/memberships"
                  />
                  <Container
                    mb={{ _: 0, md: 10 }}
                    innerProps={{
                      minHeight: { _: 0, md: "320px" },
                      pb: { _: 6, md: 4 },
                      pt: membership?.banner ? { md: 0 } : { md: 4 },
                      overflow: "hidden",
                    }}
                  >
                    {isDigitalMembership && (
                      <Flex
                        p={2}
                        w="100%"
                        alignItems="center"
                        borderRadius="sm"
                        bg="N100"
                        border="1px"
                        borderColor="N400"
                        my={3}
                      >
                        <Icon fontSize={6} icon="info" color="warning" mr={1} />
                        <Text fontSize={3} lineHeight="high" color="N800">
                          {`Please note that this is a digital ${t(
                            "membership_name"
                          ).toLowerCase()} which
                          provides you online access only`}
                        </Text>
                      </Flex>
                    )}
                    {membership?.banner && (
                      <Image
                        image={membership?.banner}
                        alt={`${membership.name} banner`}
                        priority={true}
                        // @ts-expect-error override
                        m={{ _: 0, md: "0 -32px 16px -32px" }}
                      />
                    )}

                    {showBanner && <RenewalBanner button="secondary" mb={2} />}
                    {showNonLoggedInBanner && (
                      <NonLoggedInRenewalBanner mb={2} />
                    )}

                    <Status loading={!error && !membership} error={error}>
                      {membership?.types.length > 0 && (
                        <Flex
                          flexDir={{ _: "column", md: "row" }}
                          justifyContent="flex-end"
                          mb={{ _: 2, md: "-18px" as any }}
                        >
                          <PrimaryButton
                            minH="48px"
                            display="flex"
                            w={{ _: "100%", md: "auto" }}
                            ml={{ _: 0, md: 2 }}
                            mt={2}
                            onClick={captureClick(
                              () => void router.push(membershipReservationLink),
                              InteractionNames.BUTTON_PRIMARY,
                              {
                                [AttributeNames.BUTTON_LABEL]: primaryButtonLabel,
                              }
                            )}
                          >
                            {primaryButtonLabel}
                          </PrimaryButton>
                        </Flex>
                      )}
                      <Box mx={{ _: 0, md: "-32px" as any }}>
                        {membership && <Tabs items={tabs} />}
                      </Box>
                      <Divider display={{ _: "none", md: "flex" }} mb={3} />
                      <Flex
                        flexDir={{ _: "column", md: "row" }}
                        justifyContent="flex-end"
                      >
                        {membership?.termsAndConditions && (
                          <SecondaryButton
                            to={`/memberships/${membership?.id}/terms-and-conditions`}
                            minH="48px"
                            w={{ _: "100%", md: "auto" }}
                            mb={{ _: 3, md: 0 }}
                          >
                            Full Terms &amp; Conditions
                          </SecondaryButton>
                        )}
                        <Divider mb={3} display={{ _: "flex", md: "none" }} />

                        {membership?.types.length > 0 && (
                          <PrimaryButton
                            minH="48px"
                            display="flex"
                            w={{ _: "100%", md: "auto" }}
                            ml={{ _: 0, md: 2 }}
                            onClick={captureClick(
                              () => void router.push(membershipReservationLink),
                              InteractionNames.BUTTON_PRIMARY,
                              {
                                [AttributeNames.BUTTON_LABEL]: primaryButtonLabel,
                              }
                            )}
                          >
                            {primaryButtonLabel}
                          </PrimaryButton>
                        )}
                      </Flex>
                    </Status>
                  </Container>
                </Flex>
                <Footer />
              </ContentContainer>
            </Layout>
          </Flex>
        </Flex>
      </Status>
      <BottomBar />
    </>
  );
};

export default MembershipId;
